import { get, post, patch } from 'axios';

import { ProgressByCourse, UserProgressPage } from 'types/progress';
import {
  COURSE_PROGRESS,
  EXTERNAL_SUBS_COURSE_PROGRESS,
  PROGRESS_API_URL,
  USER_PROGRESS_TOUCH_API,
  USER_PROGRESS_PAGE_INSTANCE,
  USER_PROGRESS_PAGE_RESOURCE_API,
} from '../config/constants';

// eslint-disable-next-line
export const getProgress = () => {
  return get(PROGRESS_API_URL).then((res) => res.data);
};

/**
 * Requests the summary of the progress of a manager's reportees.
 * @param organizationId - The id of the organization to which the reportees belong.
 * @param courseType - "course" or "module", depending on the kind of courses
 * we want the progress of (optional).
 * @returns The list of the progress of each user and course.
 */
export const getReporteesProgress = async (
  organizationId: string,
  users: string[],
  courseType: string = null,
  signal?: AbortSignal,
) => {
  try {
    const request = {
      organization: organizationId,
      course_type: courseType,
      users,
    };
    const response = await post(`${PROGRESS_API_URL}reportees/`, request, {
      signal,
    });
    return response.data.batch_progress;
  } catch (err) {
    throw err;
  }
};

export const setCourseProgress = (slug, pageUUID) =>
  new Promise((resolve, reject) => {
    const config = {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
    };

    const data = {
      course_slug: slug,
      entry_uuid: pageUUID,
      progress: 1,
      score: 1,
    };

    post(`${COURSE_PROGRESS}`, data, config)
      .then((res) => {
        resolve(res.data.result);
      })
      .catch((error) => reject(error));
  });

export const setCourseProgressExternalSubscription = (
  courseCode,
  progress,
  externalSubscription,
) =>
  new Promise((resolve, reject) => {
    const config = {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
    };

    const data = {
      course_code: courseCode,
      progress,
      subscription: externalSubscription,
    };

    post(`${EXTERNAL_SUBS_COURSE_PROGRESS}`, data, config)
      .then((res) => {
        resolve(res.data.result);
      })
      .catch((error) => reject(error));
  });

export const touchProgressDocument = async (courseSlug) => {
  try {
    const request = { slug: courseSlug };
    await post(USER_PROGRESS_TOUCH_API, request);
  } catch (err) {
    throw err;
  }
};

/**
 * Sends a request to update the progress of a course that consists on videos.
 * @param slug - The course's slug.
 * @param progress - An object where the keys are the ids of each of the videos
 * and the values are their progress from 0 to 1.
 * @param currentVideo - The video the user is currently watching when sending this request.
 * @param timestamp - The second of the video where the user is at the moment.
 * @returns The updated user progress document.
 */
export const updateVideoProgress = async (
  slug: String,
  progress: Object,
  currentVideo: String,
  timestamp: number,
): Promise<ProgressByCourse> => {
  try {
    const data = {
      progress,
      current_video: currentVideo,
      timestamp,
    };
    const result = await post(
      `${PROGRESS_API_URL}${slug}/update_video_progress/`,
      data,
    );
    return result.data;
  } catch (err) {
    throw err;
  }
};

export const saveUserProgressPage = async (
  newUserProgressPageData: Partial<UserProgressPage>,
  uuid: string,
): Promise<UserProgressPage> => {
  try {
    const result = await patch(
      `${USER_PROGRESS_PAGE_INSTANCE.replace('<uuid>', uuid)}/@@partial_update`,
      newUserProgressPageData,
    );
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const getUserProgressPage = async (
  uuid: string,
): Promise<UserProgressPage> => {
  try {
    const result = await get(
      `${USER_PROGRESS_PAGE_INSTANCE.replace('<uuid>', uuid)}/@@get`,
    );
    return result.data as UserProgressPage;
  } catch (e) {
    throw e;
  }
};

/**
 * A lesson can appear in multiple courses, but its progress page is unique/canonical.
 * This endpoint accepts either the course slug or the lesson slug and returns the same document.
 *
 * Examples (both resolve to the same document):
 *   /zdocr/user_progress_page/@@get_canon?content_uuid=smt-intro-lecture&user=testuser&course_slug=ad0001l-8.0
 *   /zdocr/user_progress_page/@@get_canon?content_uuid=smt-intro-lecture&user=testuser&course_slug=ad258-8.0
 */
export const getUserProgressPageCanon = async (
  contentUUID: string,
  courseSlug: string,
  username: string,
): Promise<UserProgressPage> => {
  try {
    const result = await get(`${USER_PROGRESS_PAGE_RESOURCE_API}@@get_canon`, {
      params: {
        content_uuid: contentUUID,
        user: username,
        course_slug: courseSlug,
      },
    });
    return result.data as UserProgressPage;
  } catch (e) {
    throw e;
  }
};
